import React from 'react';
import { Settings, PercentSquare, DollarSign } from 'lucide-react';

/**
 * Секция настроек бюджета
 */
const SettingsSection = ({ 
  formData,
  handleInputChange,
  errors
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-gray-500" />
        Дополнительные настройки
      </h3>

      <div className="space-y-6">
        {/* Налоги */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              id="includeTaxes"
              name="settings.includeTaxes"
              checked={formData.settings.includeTaxes}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeTaxes" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <PercentSquare className="w-4 h-4 mr-1 text-emerald-500" />
              Учитывать НДФЛ
            </label>
          </div>
          
          {formData.settings.includeTaxes && (
            <div className="ml-7">
              <label htmlFor="taxRate" className="block text-xs font-medium text-gray-700 dark:text-gray-400 mb-1">
                Налоговая ставка (%)
              </label>
              <div className="max-w-xs relative">
                <input
                  type="text"
                  id="taxRate"
                  name="settings.taxRate"
                  value={formData.settings.taxRate}
                  onChange={handleInputChange}
                  className="form-input w-full pr-6"
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">%</span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Стандартная ставка НДФЛ в России - 13%
              </p>
            </div>
          )}
        </div>

        {/* Инфляция */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              id="includeInflation"
              name="settings.includeInflation"
              checked={formData.settings.includeInflation}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeInflation" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <DollarSign className="w-4 h-4 mr-1 text-emerald-500" />
              Учитывать инфляцию
            </label>
          </div>
          
          {formData.settings.includeInflation && (
            <div className="ml-7">
              <label htmlFor="inflationRate" className="block text-xs font-medium text-gray-700 dark:text-gray-400 mb-1">
                Годовая инфляция (%)
              </label>
              <div className="max-w-xs relative">
                <input
                  type="text"
                  id="inflationRate"
                  name="settings.inflationRate"
                  value={formData.settings.inflationRate}
                  onChange={handleInputChange}
                  className="form-input w-full pr-6"
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">%</span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Прогнозная инфляция в России на 2025 год - около 4%
              </p>
            </div>
          )}
        </div>

        {/* Округление */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="roundNumbers"
              name="settings.roundNumbers"
              checked={formData.settings.roundNumbers}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="roundNumbers" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Округлять числа в расчетах
            </label>
          </div>
          {formData.settings.roundNumbers && (
            <p className="mt-1 ml-7 text-xs text-gray-500 dark:text-gray-400">
              Суммы будут округляться до целых рублей для удобства восприятия
            </p>
          )}
        </div>

        {/* Валюта */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Валюта бюджета
          </label>
          <div className="max-w-xs">
            <select
              id="currency"
              name="settings.currency"
              value={formData.settings.currency}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              <option value="RUB">Российский рубль (₽)</option>
              <option value="USD">Доллар США ($)</option>
              <option value="EUR">Евро (€)</option>
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Валюта используется для отображения, все расчеты производятся в рублях
          </p>
        </div>
      </div>

      {/* Информация о данных */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
          О ваших данных
        </h4>
        <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1 list-disc list-inside">
          <li>Все данные хранятся локально в вашем браузере</li>
          <li>Мы не собираем и не передаем информацию о вашем бюджете</li>
          <li>Вы можете экспортировать данные в формате CSV для использования в электронных таблицах</li>
          <li>Сохраненные бюджеты можно использовать для сравнения и отслеживания изменений</li>
        </ul>
      </div>
    </div>
  );
};

export default SettingsSection;